import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ACCENT, STATUS, STATUS_ORDER } from '../../styles/tokens';

export function StatusPill({ status, size = 'md' }) {
    const { t } = useTranslation();
    const s = STATUS[status] || STATUS.RECEIVED;
    const small = size === 'sm';
    return (
        <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.75, px: small ? 0.9 : 1.1, py: small ? 0.15 : 0.35,
            borderRadius: 999, bgcolor: s.bg, whiteSpace: 'nowrap',
        }}>
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: s.color }} />
            <Typography sx={{ fontSize: small ? 11.5 : 12.5, fontWeight: 700, color: s.color }}>{t(`status.${status}`)}</Typography>
        </Box>
    );
}

/** Three-step segmented control for the job status. */
export function StatusSegmented({ value, onChange, disabled }) {
    const { t } = useTranslation();
    return (
        <Box role="radiogroup" sx={{ display: 'inline-flex', p: 0.5, borderRadius: '12px', bgcolor: '#F1F5F9', gap: 0.5, flexWrap: 'wrap' }}>
            {STATUS_ORDER.map((s) => {
                const active = value === s;
                const c = STATUS[s];
                return (
                    <Box key={s} role="radio" aria-checked={active} tabIndex={0}
                         onClick={() => !disabled && !active && onChange(s)}
                         onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !disabled && !active) { e.preventDefault(); onChange(s); } }}
                         sx={{
                             display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.75, borderRadius: '9px',
                             cursor: disabled ? 'default' : 'pointer', userSelect: 'none', fontSize: 13, fontWeight: active ? 700 : 500,
                             bgcolor: active ? '#fff' : 'transparent', color: active ? c.color : '#64748B',
                             boxShadow: active ? '0 1px 3px rgba(16,42,66,0.12)' : 'none', transition: 'all 0.15s',
                             '&:hover': { color: c.color },
                             '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: 1 },
                         }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: c.color, opacity: active ? 1 : 0.45 }} />
                        {t(`status.${s}`)}
                    </Box>
                );
            })}
        </Box>
    );
}

export function FilterPill({ label, count, active, onClick, color }) {
    const c = color || ACCENT;
    return (
        <Box component="button" type="button" onClick={onClick} sx={{
            px: 1.5, py: 0.5, borderRadius: '20px', cursor: 'pointer', font: 'inherit',
            display: 'inline-flex', alignItems: 'center', gap: 0.75, fontSize: 13, fontWeight: active ? 700 : 500,
            bgcolor: active ? c : 'transparent', color: active ? '#fff' : '#64748B',
            border: '1.5px solid', borderColor: active ? c : '#E2E8F0', whiteSpace: 'nowrap', transition: 'all 0.12s',
            '&:hover': { borderColor: c, color: active ? '#fff' : c },
        }}>
            {label}
            {count != null && <Box component="span" sx={{ fontSize: 11, fontWeight: 700, opacity: active ? 0.85 : 0.6 }}>{count}</Box>}
        </Box>
    );
}

export function StatCard({ label, value, sub, icon, color = ACCENT, onClick }) {
    return (
        <Paper elevation={0} onClick={onClick} sx={{
            border: '1px solid #EEF2F6', borderRadius: '14px', px: 2.5, py: 2, flex: '1 1 190px', minWidth: 0,
            cursor: onClick ? 'pointer' : 'default', transition: 'box-shadow 0.2s, transform 0.2s',
            '&:hover': onClick ? { boxShadow: '0 12px 28px -16px rgba(30,58,82,0.3)', transform: 'translateY(-1px)' } : {},
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 1 }}>{label}</Typography>
                {icon && <Box sx={{ color, display: 'flex', opacity: 0.85 }}>{icon}</Box>}
            </Box>
            <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#0F172A', mt: 0.75, lineHeight: 1.2 }}>{value}</Typography>
            {sub && <Typography sx={{ fontSize: 12, color: '#94A3B8', mt: 0.25 }}>{sub}</Typography>}
        </Paper>
    );
}

export function CategoryTag({ category }) {
    const { t } = useTranslation();
    if (!category) return <Typography sx={{ fontSize: 12.5, color: '#CBD5E1' }}>{t('jobs.noCategory')}</Typography>;
    return (
        <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1, py: 0.25, borderRadius: '7px',
            bgcolor: `${category.color}14`, border: `1px solid ${category.color}33`, maxWidth: '100%',
        }}>
            <Box sx={{ width: 7, height: 7, borderRadius: '2px', bgcolor: category.color, flexShrink: 0 }} />
            <Typography noWrap sx={{ fontSize: 12, fontWeight: 600, color: '#334155' }}>{category.name}</Typography>
        </Box>
    );
}

export function PaidBadge({ paid }) {
    const { t } = useTranslation();
    return (
        <Typography component="span" sx={{
            fontSize: 11, fontWeight: 700, px: 0.9, py: 0.2, borderRadius: '6px', whiteSpace: 'nowrap',
            color: paid ? '#059669' : '#B45309', bgcolor: paid ? 'rgba(16,185,129,0.12)' : 'rgba(245,158,11,0.14)',
        }}>
            {paid ? t('jobs.paid') : t('jobs.unpaid')}
        </Typography>
    );
}

export function EmptyState({ icon, title, text, action }) {
    return (
        <Box sx={{ border: '1px dashed #D5DEE7', borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.7)', py: 7, px: 3, textAlign: 'center' }}>
            {icon && <Box sx={{ color: '#CBD5E1', mb: 1.5, '& svg': { fontSize: 42 } }}>{icon}</Box>}
            <Typography fontWeight={700} color="#475569">{title}</Typography>
            {text && <Typography sx={{ fontSize: 13.5, color: '#94A3B8', mt: 0.5, maxWidth: 420, mx: 'auto' }}>{text}</Typography>}
            {action && <Box sx={{ mt: 2.5 }}>{action}</Box>}
        </Box>
    );
}

export function SectionCard({ title, action, children, sx }) {
    return (
        <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: '14px', overflow: 'hidden', ...sx }}>
            {title && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 1.75, borderBottom: '1px solid #F1F5F9' }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1a2332' }}>{title}</Typography>
                    {action}
                </Box>
            )}
            {children}
        </Paper>
    );
}

/** Owner names one per line (co-owners on the job and quote pages). */
export function OwnerList({ names }) {
    return names.map((n, i) => <Box key={i}>{n}</Box>);
}

/** Initials on a colour derived from the name (client and team cards, like Plankton's customers). */
export function InitialsAvatar({ name, size = 40 }) {
    const initials = (name || '').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';
    const hue = [...(name || '')].reduce((a, c) => a + c.charCodeAt(0), 0) % 360;
    return (
        <Box sx={{
            width: size, height: size, borderRadius: '50%', flexShrink: 0, bgcolor: `hsl(${hue}, 55%, 88%)`, color: `hsl(${hue}, 55%, 32%)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.34, fontWeight: 700,
        }}>{initials}</Box>
    );
}

/** Dashed "add new" tile that opens a card grid (hidden on phones, where the header button is right above). */
export function AddTile({ label, onClick, icon, minHeight = 150 }) {
    return (
        <Box onClick={onClick} role="button" sx={{
            border: '1.5px dashed #CBD5E1', borderRadius: '14px', p: 2.5, minHeight, cursor: 'pointer',
            display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1,
            transition: 'all 0.15s', '&:hover': { borderColor: '#427898', bgcolor: '#F8FBFF' },
        }}>
            <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: '#F0F7FF', color: '#427898', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {icon}
            </Box>
            <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: '#427898' }}>{label}</Typography>
        </Box>
    );
}

export function InfoRow({ label, children }) {
    return (
        <Box sx={{ display: 'flex', gap: 2, py: 1.1, borderBottom: '1px solid #F5F7FA', '&:last-of-type': { borderBottom: 0 } }}>
            <Typography sx={{ fontSize: 12.5, color: '#94A3B8', fontWeight: 600, width: 130, flexShrink: 0 }}>{label}</Typography>
            <Box sx={{ fontSize: 13.5, color: '#1E293B', minWidth: 0, flex: 1, wordBreak: 'break-word' }}>{children ?? '—'}</Box>
        </Box>
    );
}
